import { Fragment, useCallback, useMemo, useState } from 'react';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import Table from 'shell/components/shared/table';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse, HttpResponseList } from 'shell/models/http-response';
import { useEffect } from 'react';
import { Consulate } from 'shell/models/consulate';
import { useFormik } from 'formik';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import { generatePath, useNavigate } from 'react-router-dom';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import ListHeader from 'shell/components/shared/list-header';
import { Typography } from '@mui/material';
import { PlaceVM, FiltersFormPlace } from 'civil-status/view-models/place';
import {
  validationSchema,
  authsObj,
  CommonDeathplaceCrumbs,
  headCells,
  initialValues,
} from 'civil-status/constants/place';
import { transformDataset } from 'civil-status/utils/place';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import FiltersPlace from 'civil-status/components/place/list';
import { ValueDomainType } from 'shell/models/value-domain-type';
import { DomainType } from 'civil-status/constants/domain';
import { DateTime } from 'luxon';

const DeathplaceListPage = (): React.ReactElement => {
  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<PlaceVM>>({
    element: null,
    open: false,
  });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<PlaceVM>>({
    element: null,
    open: false,
  });

  const filterTable = useFilterTable<FiltersFormPlace>();

  const [deathplaces, getDeathplaces] = useHttpFetch<
    HttpResponse<Array<ValueDomainType>>,
    Array<PlaceVM>
  >(`${__ACT_API_URL__}/valori-dominio-atti`, {
    onBootstrap: false,
    options: {
      method: 'GET',
      loading: true,
      errorCatching: true,
    },
    transformer: (response) => {
      const res: Array<PlaceVM> = response.data.payload.map((element) => ({
        id: element.key?.idValore?.toString() || '',
        description: element?.descrizioneBreveValore || '',
        orderNumber: element?.numOrdinamento?.toString() || '',
        startValidityDate: element?.dataInizioValidita
          ? DateTime.fromISO(element?.dataInizioValidita)?.toFormat('dd/MM/yyyy')
          : '',
        endValidityDate: element?.dataFineValidita
          ? DateTime.fromISO(element?.dataFineValidita)?.toFormat('dd/MM/yyyy')
          : '',
      }));
      return res;
    },
  });

  const list = (deathplaces.data as Array<PlaceVM>) || [];

  const deleteDeathplace = useHttpClient<HttpResponse<Consulate>>(
    `${__ACT_API_URL__}/valori-dominio-atti/delete/${DomainType.Deathplace}`,
    {
      options: {
        method: 'DELETE',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const onSubmit = useCallback(
    (formValues: FiltersFormPlace) => {
      const queryString = transformDataset({ ...formValues, page: 0 }, DomainType.Deathplace);
      filterForm.setFieldValue('page', 0);
      getDeathplaces({
        params: {},
        paramsSerializer: () => queryString,
      });
    },
    [getDeathplaces]
  );

  const filterForm = useFormik<FiltersFormPlace>({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit,
  });

  useEffect(() => {
    filterForm.setValues(filterTable.values || initialValues);
    const queryString = transformDataset(
      filterTable?.values || initialValues,
      DomainType.Deathplace
    );

    getDeathplaces({
      params: {},
      paramsSerializer: () => queryString,
    });
  }, []);

  useEffect(() => {
    return () => filterTable.setValues(filterForm.values);
  }, [filterForm.values]);

  const count: number = useMemo(() => {
    return list.length;
  }, [list]);

  const navigate = useNavigate();

  const auths = useActionAuthorization(authsObj);

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...filterForm.values, orders };
      const queryString = transformDataset(newDataset, DomainType.Deathplace);

      return (
        getDeathplaces({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<Consulate>, Array<PlaceVM>>
      ).then(() => {
        filterForm.setFieldValue('orders', orders);
      });
    },
    [filterForm.values, getDeathplaces, filterForm.setFieldValue]
  );

  const onVisibilityPress = useCallback(
    (cell): Promise<PlaceVM> => {
      return new Promise((resolve) => {
        navigate(
          generatePath('/stato-civile/luogo-decesso/dettaglio/:id', {
            id: cell.id,
          })
        );
        resolve(cell);
      });
    },
    [navigate]
  );

  const onEditPress = useCallback(
    (cell): Promise<PlaceVM> => {
      return new Promise((resolve) => {
        navigate(
          generatePath('/stato-civile/luogo-decesso/modifica/:id', {
            id: cell.id,
          })
        );
        resolve(cell);
      });
    },
    [navigate]
  );

  const onTrashPress = useCallback(
    (cell): Promise<PlaceVM> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<PlaceVM>;
    },
    [setDeleteAlertModal]
  );

  const handleChangePage = useCallback(
    (page) => {
      filterForm.setFieldValue('page', page || 0);
    },
    [filterForm.setFieldValue]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = { ...filterForm.values, rowsPerPage: rowsPerPage || 0, page: 0 };
      filterForm.setValues(newDataset);
    },
    [filterForm.setFieldValue, filterForm.values]
  );

  return (
    <Fragment>
      <Breadcrumbs items={CommonDeathplaceCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuovo luogo decesso"
        addBtnLabel="Aggiungi nuovo luogo decesso"
        canAdd
        onAddBtnClick={() => {
          navigate('/stato-civile/luogo-decesso/nuovo');
        }}
        title="Ricerca luogo decesso"
      />
      <div className="pmi-my-3">
        <FiltersPlace filterForm={filterForm} />
      </div>
      {deathplaces.data ? (
        !(list.length > 0) ? (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        ) : (
          <Table
            onSort={onSort}
            head={headCells}
            orders={filterForm.values.orders}
            activeActions={auths}
            page={filterForm.values.page}
            rowsPerPage={filterForm.values.rowsPerPage}
            body={list}
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            count={count || 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista luoghi decesso"
            noSort
          />
        )
      ) : undefined}
      <DeleteAlertModal
        title="Cancellazione luogo decesso"
        message={`Confermare la cancellazione del luogo decesso ${
          deleteAlertModal.element?.description || ''
        }?`}
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          (
            deleteDeathplace({ url: `/${deleteAlertModal.element?.id}` }) as ResponsePromise<void>
          ).then(() => {
            setDeleteAlertModal({ open: false, element: null });
            setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
          });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione luogo decesso"
        message={`La cancellazione del luogo decesso ${
          deleteSuccessModal.element?.description || ''
        } è avvenuta con successo`}
        onClose={() => {
          const queryString = transformDataset(filterForm.values, DomainType.Deathplace);

          (
            getDeathplaces({
              params: {},
              paramsSerializer: () => queryString,
            }) as ResponsePromise<HttpResponseList<Consulate>, Array<PlaceVM>>
          ).then(() => setDeleteSuccessModal({ open: false, element: null }));
        }}
        onOk={() => {
          const queryString = transformDataset(filterForm.values, DomainType.Deathplace);

          (
            getDeathplaces({
              params: {},
              paramsSerializer: () => queryString,
            }) as ResponsePromise<HttpResponseList<Consulate>, Array<PlaceVM>>
          ).then(() => setDeleteSuccessModal({ open: false, element: null }));
        }}
      />
    </Fragment>
  );
};

DeathplaceListPage.displayName = 'Deathplace List page component';

export default DeathplaceListPage;
